import apiClient from './api.service';

function unwrapBody<T>(res: unknown): T {
  if (res == null || typeof res !== 'object') return res as T;
  const r = res as Record<string, unknown>;
  const inner = r.data;
  if (inner != null && typeof inner === 'object' && 'data' in (inner as object)) {
    return (inner as { data: T }).data;
  }
  if ('data' in r && (r.success === true || r.status === 'success')) {
    return r.data as T;
  }
  return res as T;
}

// =====================================================
// CALIBRATION TYPES
// =====================================================
export interface CalibrationSchedule {
  id: string;
  company_id: string;
  equipment_id?: string;
  equipment_code?: string;
  equipment_name?: string;
  calibration_code: string;
  calibration_type?: string;
  calibration_standard?: string;
  /** Tolerance / pass-fail criteria (Q1) */
  acceptance_criteria?: string;
  calibration_frequency_days: number;
  last_calibration_date?: string | null;
  next_calibration_date?: string | null;
  calibration_due_date?: string | null;
  calibration_provider?: string;
  calibration_location?: string;
  status: 'Scheduled' | 'In Progress' | 'Completed' | 'Overdue' | 'Cancelled';
  is_critical: boolean;
  calibration_kind?: CalibrationKind;
  working_range?: string | null;
  accuracy?: string | null;
  measurement_unit?: string | null;
  interval_basis?: IntervalBasis;
  first_use_date?: string | null;
  supplier_id?: string | null;
  supplier_name?: string | null;
  external_limitations?: string | null;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export type CalibrationKind = 'CALIBRATION' | 'VERIFICATION' | 'SOFTWARE_VALIDATION';
export type IntervalBasis = 'CALENDAR' | 'FIRST_USE';
export type TraceabilityBasis = 'NATIONAL_STANDARD' | 'MANUFACTURER_SPEC';
export type QualityReviewStatus = 'PENDING' | 'ACCEPTED' | 'REJECTED';
export type TmmdeStatus =
  | 'NOT_REQUIRED'
  | 'PENDING'
  | 'CALIBRATED'
  | 'DUE_SOON'
  | 'OVERDUE'
  | 'NOT_FOR_USE'
  | 'QUARANTINED';
export type TmmdeStatusAction = 'NOT_FOR_USE' | 'QUARANTINE' | 'RELEASE';

export interface CalibrationReading {
  id?: string;
  test_point?: string | null;
  nominal_value?: string | null;
  tolerance?: string | null;
  as_found?: string | null;
  as_left?: string | null;
  unit?: string | null;
  result?: 'PASS' | 'FAIL' | null;
  remarks?: string | null;
}

export interface CalibrationRecord {
  id: string;
  company_id: string;
  calibration_schedule_id?: string;
  equipment_id?: string;
  /** Present when list/detail load includes schedule relation */
  calibration_schedule?: Pick<
    CalibrationSchedule,
    'id' | 'calibration_code' | 'equipment_id' | 'equipment_code' | 'equipment_name'
  >;
  calibration_number: string;
  calibration_date: string;
  calibration_type?: string;
  calibration_provider?: string;
  calibrator_name?: string;
  calibration_standard?: string;
  calibration_location?: string;
  result: 'Pass' | 'Fail' | 'Conditional' | 'Pending';
  as_found_reading?: string;
  as_left_reading?: string;
  calibration_certificate_number?: string;
  calibration_certificate_document_id?: string;
  calibration_valid_until: string;
  next_calibration_due_date: string;
  status: 'Completed' | 'Failed' | 'Conditional' | 'Pending';
  equipment_status: 'In Service' | 'Out of Service' | 'Quarantined';
  calibration_notes?: string;
  failure_reason?: string;
  corrective_action?: string;
  supplier_id?: string | null;
  supplier_name?: string | null;
  traceability_basis?: TraceabilityBasis | null;
  measurement_uncertainty?: string | null;
  environmental_conditions?: string | null;
  adjusted?: boolean | number;
  quality_review_status?: QualityReviewStatus;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  review_remarks?: string | null;
  readings?: CalibrationReading[];
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface TmmdeEquipmentRow {
  id: string;
  asset_code: string;
  asset_name: string;
  serial_number?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  equipment_ownership: string;
  calibration_status: TmmdeStatus;
  calibration_due_date?: string | null;
  last_calibration_date?: string | null;
  identification_method?: string | null;
  not_for_use_reason?: string | null;
  warehouse_id?: string | null;
  warehouse_name?: string | null;
  site?: string | null;
  building?: string | null;
  room?: string | null;
  asset_status?: string | null;
  schedule_id?: string | null;
  calibration_code?: string | null;
  calibration_kind?: CalibrationKind | null;
  calibration_frequency_days?: number | null;
  interval_basis?: IntervalBasis | null;
  first_use_date?: string | null;
  acceptance_criteria?: string | null;
  accuracy?: string | null;
  working_range?: string | null;
  measurement_unit?: string | null;
  calibration_standard?: string | null;
  calibration_type?: string | null;
  calibration_provider?: string | null;
  supplier_id?: string | null;
  supplier_name?: string | null;
  is_critical?: number | boolean | null;
  days_to_due?: number | null;
  record_count_window?: number | string;
  fail_count_window?: number | string;
}

export interface TmmdeEquipmentSummary {
  total: number;
  by_status: Partial<Record<TmmdeStatus, number>>;
}

export interface TmmdeStatusEvent {
  id: string;
  from_status?: string | null;
  to_status: string;
  reason?: string | null;
  source_type?: string | null;
  source_id?: string | null;
  changed_by?: string | null;
  changed_by_name?: string | null;
  changed_at: string;
}

export interface TmmdeExternalVerification {
  id: string;
  ownership?: string | null;
  physical_condition_ok: number | boolean;
  physical_condition_notes?: string | null;
  certificate_verified: number | boolean;
  certificate_reference?: string | null;
  certificate_valid_until?: string | null;
  suitable_for_use: number | boolean;
  limitations?: string | null;
  outcome: 'ACCEPTED' | 'REJECTED';
  remarks?: string | null;
  verified_by_name?: string | null;
  verified_at: string;
}

export interface TmmdeEquipmentDetail {
  equipment: TmmdeEquipmentRow;
  schedules: CalibrationSchedule[];
  records: CalibrationRecord[];
  status_events: TmmdeStatusEvent[];
  external_verifications: TmmdeExternalVerification[];
  usable: boolean;
}

export type TmmdeUsageSourceType = 'FIR_REPORT' | 'FIR_REQUEST' | 'STAGE_INSPECTION' | 'CSPM_ALLOCATION';

export interface TmmdeUsageEntry {
  id: string;
  asset_id: string;
  source_type: TmmdeUsageSourceType;
  source_id: string;
  source_ref?: string | null;
  calibration_status_at_use: TmmdeStatus;
  calibration_due_date_at_use?: string | null;
  override_used: number | boolean;
  override_reason?: string | null;
  used_by?: string | null;
  used_by_name?: string | null;
  used_at: string;
}

export type TmmdeHoldCause = 'OUT_OF_CALIBRATION' | 'DAMAGED' | 'LOST' | 'OTHER';
export type TmmdeImpactItemType = 'INSPECTION' | 'WORK_ORDER' | 'DELIVERY_NOTE' | 'CUSTOMER';
export type TmmdeImpactDisposition = 'PENDING' | 'NO_IMPACT' | 'REINSPECT' | 'NOTIFY_CUSTOMER';

export interface TmmdeImpactItem {
  id: string;
  item_type: TmmdeImpactItemType;
  source_type?: TmmdeUsageSourceType | null;
  source_id?: string | null;
  source_ref?: string | null;
  work_order_id?: string | null;
  work_order_number?: string | null;
  delivery_note_id?: string | null;
  delivery_note_number?: string | null;
  customer_id?: string | null;
  customer_name?: string | null;
  label: string;
  used_at?: string | null;
  override_used: number | boolean;
  disposition: TmmdeImpactDisposition;
  disposition_notes?: string | null;
  dispositioned_by_name?: string | null;
  dispositioned_at?: string | null;
  notified_at?: string | null;
  notified_contact?: string | null;
  notification_notes?: string | null;
  notified_by_name?: string | null;
  ncr_id?: string | null;
  ncr_number?: string | null;
}

export interface TmmdeImpactAssessment {
  id: string;
  assessment_number: string;
  asset_id: string;
  asset_code?: string;
  asset_name?: string;
  serial_number?: string | null;
  calibration_status?: TmmdeStatus;
  trigger_type: string;
  trigger_reason?: string | null;
  period_from?: string | null;
  period_to: string;
  status: 'OPEN' | 'CLOSED';
  summary?: string | null;
  ncr_id?: string | null;
  ncr_number?: string | null;
  created_at: string;
  created_by_name?: string | null;
  closed_at?: string | null;
  closed_by_name?: string | null;
  closure_notes?: string | null;
  item_count?: number | string;
  pending_count?: number | string;
  customer_count?: number | string;
  items?: TmmdeImpactItem[];
}

export interface TmmdeKpis {
  on_time_percent: number | null;
  on_time_basis: number;
  failure_rate_percent: number | null;
  calibrations_last_12_months: number;
  overdue: number;
  open_impact_assessments: number;
  interval_review: Array<{
    asset_id: string;
    asset_code: string;
    asset_name: string;
    failures: number;
    calibration_frequency_days: number | null;
    window_months: number;
  }>;
}

export interface TmmdeEquipmentFilters {
  status?: string;
  ownership?: string;
  warehouse_id?: string;
  due_within_days?: number;
  q?: string;
}

export interface ExternalVerificationInput {
  physical_condition_ok: boolean;
  physical_condition_notes?: string | null;
  certificate_verified: boolean;
  certificate_reference?: string | null;
  certificate_valid_until?: string | null;
  suitable_for_use: boolean;
  limitations?: string | null;
  remarks?: string | null;
}

export type CalibrationRecordInput = Partial<CalibrationRecord> & { readings?: CalibrationReading[] };

// =====================================================
// CALIBRATION SERVICE
// =====================================================
class CalibrationService {
  async getCalibrationSchedules(filters?: { status?: string; equipment_id?: string }): Promise<CalibrationSchedule[]> {
    const response = await apiClient.get('/calibration/calibration-schedules', { params: filters });
    const list = unwrapBody<CalibrationSchedule[] | undefined>(response);
    return Array.isArray(list) ? list : [];
  }

  async getCalibrationScheduleById(id: string): Promise<CalibrationSchedule> {
    const response = await apiClient.get(`/calibration/calibration-schedules/${id}`);
    return unwrapBody<CalibrationSchedule>(response);
  }

  async createCalibrationSchedule(data: Partial<CalibrationSchedule>): Promise<CalibrationSchedule> {
    const response = await apiClient.post('/calibration/calibration-schedules', data);
    return unwrapBody<CalibrationSchedule>(response);
  }

  async updateCalibrationSchedule(id: string, data: Partial<CalibrationSchedule>): Promise<CalibrationSchedule> {
    const response = await apiClient.put(`/calibration/calibration-schedules/${id}`, data);
    return unwrapBody<CalibrationSchedule>(response);
  }

  async cancelCalibrationSchedule(id: string, reason?: string): Promise<CalibrationSchedule> {
    const response = await apiClient.post(`/calibration/calibration-schedules/${id}/cancel`, { reason });
    return unwrapBody<CalibrationSchedule>(response);
  }

  async getCalibrationRecords(filters?: {
    status?: string;
    result?: string;
    equipment_id?: string;
    quality_review_status?: QualityReviewStatus;
  }): Promise<CalibrationRecord[]> {
    const response = await apiClient.get('/calibration/calibration-records', { params: filters });
    const list = unwrapBody<CalibrationRecord[] | undefined>(response);
    return Array.isArray(list) ? list : [];
  }

  async getCalibrationRecordById(id: string): Promise<CalibrationRecord> {
    const response = await apiClient.get(`/calibration/calibration-records/${id}`);
    return unwrapBody<CalibrationRecord>(response);
  }

  async createCalibrationRecord(data: CalibrationRecordInput): Promise<CalibrationRecord> {
    const response = await apiClient.post('/calibration/calibration-records', data);
    return unwrapBody<CalibrationRecord>(response);
  }

  async updateCalibrationRecord(id: string, data: CalibrationRecordInput): Promise<CalibrationRecord> {
    const response = await apiClient.put(`/calibration/calibration-records/${id}`, data);
    return unwrapBody<CalibrationRecord>(response);
  }

  async reviewCalibrationRecord(
    id: string,
    decision: 'ACCEPT' | 'REJECT',
    remarks?: string
  ): Promise<CalibrationRecord> {
    const response = await apiClient.post(`/calibration/calibration-records/${id}/review`, { decision, remarks });
    return unwrapBody<CalibrationRecord>(response);
  }

  async listEquipment(
    filters?: TmmdeEquipmentFilters
  ): Promise<{ rows: TmmdeEquipmentRow[]; summary: TmmdeEquipmentSummary }> {
    const response = await apiClient.get('/calibration/equipment', { params: filters });
    const body = unwrapBody<{ rows?: TmmdeEquipmentRow[]; summary?: TmmdeEquipmentSummary } | undefined>(response);
    return {
      rows: Array.isArray(body?.rows) ? body!.rows : [],
      summary: body?.summary ?? { total: 0, by_status: {} },
    };
  }

  async getEquipment(assetId: string): Promise<TmmdeEquipmentDetail> {
    const response = await apiClient.get(`/calibration/equipment/${assetId}`);
    return unwrapBody<TmmdeEquipmentDetail>(response);
  }

  async getEquipmentUsage(assetId: string): Promise<TmmdeUsageEntry[]> {
    const response = await apiClient.get(`/calibration/equipment/${assetId}/usage`);
    const rows = unwrapBody<TmmdeUsageEntry[] | undefined>(response);
    return Array.isArray(rows) ? rows : [];
  }

  async changeEquipmentStatus(
    assetId: string,
    action: TmmdeStatusAction,
    reason?: string,
    cause?: TmmdeHoldCause
  ): Promise<{ calibration_status: TmmdeStatus }> {
    const response = await apiClient.post(`/calibration/equipment/${assetId}/status`, { action, reason, cause });
    return unwrapBody<{ calibration_status: TmmdeStatus }>(response);
  }

  async getEquipmentKpis(): Promise<TmmdeKpis> {
    const response = await apiClient.get('/calibration/equipment-kpis');
    return unwrapBody<TmmdeKpis>(response);
  }

  async listImpactAssessments(filters?: { status?: string; asset_id?: string }): Promise<TmmdeImpactAssessment[]> {
    const response = await apiClient.get('/calibration/impact-assessments', { params: filters });
    const rows = unwrapBody<TmmdeImpactAssessment[] | undefined>(response);
    return Array.isArray(rows) ? rows : [];
  }

  async getImpactAssessment(id: string): Promise<TmmdeImpactAssessment> {
    const response = await apiClient.get(`/calibration/impact-assessments/${id}`);
    return unwrapBody<TmmdeImpactAssessment>(response);
  }

  async updateImpactSummary(id: string, summary: string | null): Promise<TmmdeImpactAssessment> {
    const response = await apiClient.put(`/calibration/impact-assessments/${id}`, { summary });
    return unwrapBody<TmmdeImpactAssessment>(response);
  }

  async dispositionImpactItem(
    id: string,
    itemId: string,
    data: { disposition: TmmdeImpactDisposition; notes?: string | null }
  ): Promise<TmmdeImpactAssessment> {
    const response = await apiClient.post(`/calibration/impact-assessments/${id}/items/${itemId}/disposition`, data);
    return unwrapBody<TmmdeImpactAssessment>(response);
  }

  async recordImpactNotification(
    id: string,
    itemId: string,
    data: { notified_at: string; contact: string; notes?: string | null }
  ): Promise<TmmdeImpactAssessment> {
    const response = await apiClient.post(`/calibration/impact-assessments/${id}/items/${itemId}/notification`, data);
    return unwrapBody<TmmdeImpactAssessment>(response);
  }

  async raiseImpactNcr(
    id: string,
    data: { item_id?: string | null; severity?: string; description?: string | null }
  ): Promise<{ ncr_id: string; ncr_number: string }> {
    const response = await apiClient.post(`/calibration/impact-assessments/${id}/ncr`, data);
    return unwrapBody<{ ncr_id: string; ncr_number: string }>(response);
  }

  async refreshImpactAssessment(id: string): Promise<TmmdeImpactAssessment> {
    const response = await apiClient.post(`/calibration/impact-assessments/${id}/refresh`, {});
    return unwrapBody<TmmdeImpactAssessment>(response);
  }

  async closeImpactAssessment(id: string, closure_notes: string): Promise<TmmdeImpactAssessment> {
    const response = await apiClient.post(`/calibration/impact-assessments/${id}/close`, { closure_notes });
    return unwrapBody<TmmdeImpactAssessment>(response);
  }

  async recordExternalVerification(
    assetId: string,
    data: ExternalVerificationInput
  ): Promise<{ id: string; outcome: 'ACCEPTED' | 'REJECTED' }> {
    const response = await apiClient.post(`/calibration/equipment/${assetId}/external-verification`, data);
    return unwrapBody<{ id: string; outcome: 'ACCEPTED' | 'REJECTED' }>(response);
  }
}

export const calibrationService = new CalibrationService();

